import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { api } from '../utils/api';
import { SiteMap } from '../components/dashboard/SiteMap';
import { UnitType, UnitStatus } from '../types';
import { SearchableSelect } from '../components/layout/SearchableSelect';
import { UnitLifecycleActions } from '../components/UnitLifecycleActions';
import { computeWaterDepthCm, computeTankVolumeLiters, computePercentFilled, waterReadingGapLabel } from '@shared/metrics';
import './Sites.css';

function formatTimeAgo(dateString: string): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 0) return 'Just now'; // Handle clock drift

  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

type Tab = 'home' | 'users' | 'units' | 'config';

const CADENCE_OPTIONS = [
  { value: 600, label: '10 minutes' },
  { value: 1200, label: '20 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 7200, label: '2 hours' },
  { value: 14400, label: '4 hours' },
  { value: 21600, label: '6 hours' },
  { value: 28800, label: '8 hours' },
  { value: 43200, label: '12 hours' },
  { value: 86400, label: '24 hours' },
] as const;

export function Sites() {
  const { sites, units, activeOrganization, isSuperAdmin, currentUserId, userRoles, refetch } = useDashboardData();
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  const location = useLocation();
  const navigate = useNavigate();
  const [addUnitOpen, setAddUnitOpen] = useState(false);
  const [addUnitSerial, setAddUnitSerial] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const siteId = params.get('siteId');
    const tab = params.get('tab') as Tab;
    if (siteId) {
      setSelectedSiteId(siteId);
      if (tab && ['home', 'users', 'units', 'config'].includes(tab)) {
        setActiveTab(tab);
      } else {
        setActiveTab('home');
      }
    } else if (sites && sites.length > 0) {
      if (!selectedSiteId || !sites.some(s => s.id === selectedSiteId)) {
        setSelectedSiteId(sites[0].id);
        setActiveTab('home');
      }
    } else {
      setSelectedSiteId('');
    }
  }, [location.search, sites]);
  
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'Viewer' | 'LocalAdmin'>('Viewer');
  const [inviteMessage, setInviteMessage] = useState('');
  const [users, setUsers] = useState<any[]>([]);

  const [unadoptedUnits, setUnadoptedUnits] = useState<any[]>([]);
  const [showAdoptModal, setShowAdoptModal] = useState(false);
  const [selectedUnadoptedUnits, setSelectedUnadoptedUnits] = useState<string[]>([]);

  const [editingUnitId, setEditingUnitId] = useState<string>('');
  const [configForm, setConfigForm] = useState({
    nodeName: '',
    tankShape: 'cylindrical_vertical',
    diameter: 0,
    height: 0,
    sensorMountHeight: 0,
    latitude: 0,
    longitude: 0,
    maxBatteryVoltage: 4.2,
    cadenceSeconds: 3600
  });

  const [siteConfig, setSiteConfig] = useState({ name: '' });

  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');

  // Units spreadsheet sorting & filtering states
  const [filterNodeName, setFilterNodeName] = useState('');
  const [filterVolume, setFilterVolume] = useState('');
  const [filterVoltage, setFilterVoltage] = useState('');
  const [sortField, setSortField] = useState<'nodeName' | 'volume' | 'voltage' | 'lastSeenAt' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  // Users spreadsheet filtering & sorting states
  const [filterFirstName, setFilterFirstName] = useState('');
  const [filterLastName, setFilterLastName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [sortUserField, setSortUserField] = useState<'firstName' | 'lastName' | 'email' | 'role' | null>(null);
  const [sortUserDirection, setSortUserDirection] = useState<'asc' | 'desc' | null>(null);

  // Custom modal action states for Users
  const [confirmAction, setConfirmAction] = useState<{
    type: 'revoke' | 'promote';
    userId: string;
    email: string;
  } | null>(null);
  const [confirmDeleteSite, setConfirmDeleteSite] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const activeSite = sites.find(s => s.id === selectedSiteId);
  const siteUnits = units.filter(u => u.siteId === selectedSiteId && u.status === UnitStatus.Adopted);

  // Automatically select unit to edit if unitId query param is specified
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlUnitId = params.get('unitId');
    if (urlUnitId && siteUnits.length > 0 && editingUnitId !== urlUnitId) {
      const u = siteUnits.find(su => su.unitId === urlUnitId);
      if (u) {
        handleEditUnitSelect(urlUnitId);
      }
    }
  }, [location.search, siteUnits, editingUnitId]);

  // Sorting handlers
  const toggleSort = (field: 'nodeName' | 'volume' | 'voltage' | 'lastSeenAt') => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleUserSort = (field: 'firstName' | 'lastName' | 'email' | 'role') => {
    if (sortUserField === field) {
      if (sortUserDirection === 'asc') {
        setSortUserDirection('desc');
      } else {
        setSortUserField(null);
        setSortUserDirection(null);
      }
    } else {
      setSortUserField(field);
      setSortUserDirection('asc');
    }
  };

  // Units spreadsheet filtering and sorting selector
  const filteredAndSortedUnits = useMemo(() => {
    const computed = siteUnits.map(unit => {
      const isMobileStation = unit.unitType === UnitType.MobileStation;
      // Shared metric math — single source of truth. currentLevel is mm.
      let volumeLiters: number | null = null;
      let percentFilled: number | null = null;

      if (isMobileStation) {
        const depthCm = computeWaterDepthCm(unit.currentLevel, unit);
        if (depthCm !== null) {
          const v = computeTankVolumeLiters(depthCm, unit);
          const p = computePercentFilled(depthCm, unit);
          volumeLiters = v === null ? null : Math.round(v);
          percentFilled = p === null ? null : Math.round(p);
        }
      }

      const volumeText = !isMobileStation 
        ? '—' 
        : (volumeLiters !== null ? `${volumeLiters.toLocaleString()} L (${percentFilled}%)` : waterReadingGapLabel(unit, unit.currentLevel));

      // volumeSortValue helper: repeaters at bottom, config pending above them, then by volume
      let volumeSortValue = -1;
      if (!isMobileStation) {
        volumeSortValue = -2;
      } else if (volumeLiters !== null) {
        volumeSortValue = volumeLiters;
      }

      const voltageText = `${(unit.currentVoltage || 0).toFixed(2)} V`;

      return {
        ...unit,
        isMobileStation,
        volumeLiters,
        percentFilled,
        volumeText,
        volumeSortValue,
        voltageText,
      };
    });

    const filtered = computed.filter(unit => {
      const nodeNameMatch = (unit.nodeName || '').toLowerCase().includes(filterNodeName.toLowerCase()) ||
                            String(unit.nodeId || '').toLowerCase().includes(filterNodeName.toLowerCase()) ||
                            (unit.isMobileStation ? 'mobile station' : 'repeater').includes(filterNodeName.toLowerCase());

      const volumeMatch = unit.volumeText.toLowerCase().includes(filterVolume.toLowerCase());
      const voltageMatch = unit.voltageText.toLowerCase().includes(filterVoltage.toLowerCase());

      return nodeNameMatch && volumeMatch && voltageMatch;
    });

    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let comparison = 0;
        if (sortField === 'nodeName') {
          comparison = (a.nodeName || '').localeCompare(b.nodeName || '', undefined, { numeric: true, sensitivity: 'base' });
        } else if (sortField === 'volume') {
          comparison = a.volumeSortValue - b.volumeSortValue;
        } else if (sortField === 'voltage') {
          comparison = (a.currentVoltage || 0) - (b.currentVoltage || 0);
        } else if (sortField === 'lastSeenAt') {
          comparison = new Date(a.lastSeenAt || 0).getTime() - new Date(b.lastSeenAt || 0).getTime();
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [siteUnits, filterNodeName, filterVolume, filterVoltage, sortField, sortDirection]);

  // Users spreadsheet filtering and sorting selector
  const filteredAndSortedUsers = useMemo(() => {
    return [...users]
      .filter(u => {
        const firstName = u.firstName || '';
        const lastName = u.lastName || '';
        const email = u.email || '';
        const roleDisplay = u.role === 'OrgAdmin' ? 'Org Admin' : u.role === 'LocalAdmin' ? 'Local Admin' : u.role === 'Viewer' ? 'Viewer' : u.role || '';

        return (
          firstName.toLowerCase().includes(filterFirstName.toLowerCase()) &&
          lastName.toLowerCase().includes(filterLastName.toLowerCase()) &&
          email.toLowerCase().includes(filterEmail.toLowerCase()) &&
          roleDisplay.toLowerCase().includes(filterRole.toLowerCase())
        );
      })
      .sort((a, b) => {
        if (!sortUserField || !sortUserDirection) return 0;

        let valA = '';
        let valB = '';

        if (sortUserField === 'role') {
          valA = a.role === 'OrgAdmin' ? 'Org Admin' : a.role === 'LocalAdmin' ? 'Local Admin' : a.role === 'Viewer' ? 'Viewer' : a.role || '';
          valB = b.role === 'OrgAdmin' ? 'Org Admin' : b.role === 'LocalAdmin' ? 'Local Admin' : b.role === 'Viewer' ? 'Viewer' : b.role || '';
        } else {
          valA = a[sortUserField] || '';
          valB = b[sortUserField] || '';
        }

        const comparison = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        return sortUserDirection === 'asc' ? comparison : -comparison;
      });
  }, [users, filterFirstName, filterLastName, filterEmail, filterRole, sortUserField, sortUserDirection]);

  useEffect(() => {
    if (activeSite) {
      setSiteConfig({ name: activeSite.name });
      fetchUnadopted();
    }
  }, [activeSite]);

  useEffect(() => {
    if (siteUnits.length > 0) {
      const sorted = [...siteUnits].sort((a, b) => (a.nodeName || '').localeCompare(b.nodeName || '', undefined, { numeric: true, sensitivity: 'base' }));
      const firstUnitId = sorted[0].unitId;
      
      if (!editingUnitId || !siteUnits.some(u => u.unitId === editingUnitId)) {
        setEditingUnitId(firstUnitId);
        setConfigForm({
          nodeName: sorted[0].nodeName || '',
          tankShape: sorted[0].tankShape || 'cylindrical_vertical',
          diameter: sorted[0].tankDimensions?.diameter || 0,
          height: sorted[0].tankDimensions?.height || 0,
          sensorMountHeight: sorted[0].sensorMountHeight || 0,
          latitude: sorted[0].location?.lat || 0,
          longitude: sorted[0].location?.lng || 0,
          maxBatteryVoltage: sorted[0].maxBatteryVoltage || 4.2,
          cadenceSeconds: sorted[0].cadenceSeconds ?? 3600
        });
      }
    } else {
      setEditingUnitId('');
    }
  }, [selectedSiteId, siteUnits, editingUnitId]);

  const getReqOptions = () => {
    return activeSite?.tenantId === 'UNASSIGNED' ? { headers: { 'X-Tenant-ID': 'UNASSIGNED' } } : {};
  };

  const fetchUnadopted = async () => {
    if (!activeSite) return;
    try {
      const result = await api.get(`/api/v1/sites/${activeSite.id}/unadopted`, getReqOptions());
      setUnadoptedUnits(result || []);
    } catch (err) {
      console.error('Failed to fetch unadopted', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'users' && activeOrganization && activeSite) {
      fetchUsers();
    }
  }, [activeTab, activeOrganization, activeSite]);

  const fetchUsers = async () => {
    try {
      const siteUsers = await api.get(`/api/v1/tenants/me/users?siteId=${activeSite!.id}`);
      setUsers(siteUsers);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await api.post('/api/v1/invitations', {
        email: inviteEmail,
        role: inviteRole,
        siteId: activeSite?.id,
        tenantId: activeSite?.tenantId !== 'UNASSIGNED' ? activeSite?.tenantId : undefined
      });
      setInviteMessage(`Invitation sent! Link: ${result.link}`);
      setInviteEmail('');
      fetchUsers();
    } catch (err: any) {
      setInviteMessage(`Error: ${err.message}`);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (confirmAction.type === 'revoke') {
        await api.delete(`/api/v1/tenants/me/users/${confirmAction.userId}?siteId=${activeSite?.id}`);
      } else if (confirmAction.type === 'promote') {
        await api.put(`/api/v1/tenants/me/users/${confirmAction.userId}/role`, {
          role: 'LocalAdmin',
          siteId: activeSite?.id
        });
      }
      setConfirmAction(null);
      fetchUsers();
    } catch (err: any) {
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openAdoptModal = () => {
    setSelectedUnadoptedUnits(unadoptedUnits.map(u => u.unitId || u.SK?.replace('UNIT#', '')));
    setShowAdoptModal(true);
  };

  const handleAdopt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSite || selectedUnadoptedUnits.length === 0) return;
    try {
      await Promise.all(selectedUnadoptedUnits.map(unitId => {
        const parts = unitId.split('_');
        const rawNodeId = parts.length > 1 ? parts[parts.length - 1] : unitId;
        const u = unadoptedUnits.find(un => (un.unitId || un.SK?.replace('UNIT#', '')) === unitId);
        const nameToUse = u?.telemetryNodeName || u?.nodeName || `Node ${rawNodeId}`;
        return api.post(`/api/v1/sites/${activeSite.id}/units/${unitId}/adopt`, {
          nodeName: nameToUse,
          unitType: UnitType.MobileStation,
          tankShape: 'cylindrical_vertical',
          tankDimensions: { diameter: 100, height: 100 },
          sensorMountHeight: 0
        }, getReqOptions());
      }));
      setShowAdoptModal(false);
      refetch();
      fetchUnadopted();
    } catch (err: any) {
      alert(`Failed to adopt units: ${err.message}`);
    }
  };

  const handleEditUnitSelect = (unitId: string) => {
    setEditingUnitId(unitId);
    const u = siteUnits.find(su => su.unitId === unitId);
    if (u) {
      setConfigForm({
        nodeName: u.nodeName || '',
        tankShape: u.tankShape || 'cylindrical_vertical',
        diameter: u.tankDimensions?.diameter || 0,
        height: u.tankDimensions?.height || 0,
        sensorMountHeight: u.sensorMountHeight || 0,
        latitude: u.location?.lat || 0,
        longitude: u.location?.lng || 0,
        maxBatteryVoltage: u.maxBatteryVoltage || 4.2,
        cadenceSeconds: u.cadenceSeconds ?? 3600
      });
    }
  };

  const handleSaveUnitConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const selectedUnit = siteUnits.find(u => u.unitId === editingUnitId);
      await api.put(`/api/v1/units/${editingUnitId}`, {
        nodeName: configForm.nodeName,
        tankShape: configForm.tankShape,
        tankDimensions: { diameter: Number(configForm.diameter), height: Number(configForm.height) },
        sensorMountHeight: Number(configForm.sensorMountHeight),
        location: { lat: Number(configForm.latitude), lng: Number(configForm.longitude) },
        maxBatteryVoltage: Number(configForm.maxBatteryVoltage)
      }, getReqOptions());
      let cadenceResult: { cadenceSeconds: number; refreshPublished: boolean } | undefined;
      if (
        selectedUnit?.role !== 'leaf' &&
        Number(configForm.cadenceSeconds) !== (selectedUnit?.cadenceSeconds ?? 3600)
      ) {
        cadenceResult = await api.put(`/api/v1/units/${editingUnitId}/cadence`, {
          cadenceSeconds: Number(configForm.cadenceSeconds),
        }, getReqOptions());
      }
      alert(
        cadenceResult && !cadenceResult.refreshPublished
          ? 'Configuration saved. The reporting interval will reach the unit at its next check-in.'
          : 'Configuration saved',
      );
      refetch();
    } catch (err: any) {
      alert(`Failed to save configuration: ${err.message}`);
    }
  };

  const handleSaveSiteConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSite) return;
    try {
      await api.put(`/api/v1/sites/${activeSite.id}`, {
        name: siteConfig.name
      }, getReqOptions());
      alert('Site saved');
      refetch();
    } catch (err: any) {
      alert(`Failed to save site: ${err.message}`);
    }
  };

  const handleDeleteSite = async () => {
    if (!activeSite) return;
    try {
      await api.delete(`/api/v1/sites/${activeSite.id}`, getReqOptions());
      setSelectedSiteId('');
      setConfirmDeleteSite(false);
      refetch();
    } catch (err: any) {
      alert(`Failed to delete site: ${err.message}`);
    }
  };

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const site = await api.post('/api/v1/sites', { name: newSiteName });
      
      if (activeOrganization) {
        const siteId = site.SK.replace('SITE#', '');
        await api.post(`/api/v1/sites/${siteId}/assign`, {
          tenantId: activeOrganization.tenantId
        });
        alert(`Site created and automatically assigned to ${activeOrganization.name}!`);
        setShowCreateSiteModal(false);
        setNewSiteName('');
        refetch();
        setSelectedSiteId(siteId);
      } else {
        alert('Site created successfully! It is currently UNASSIGNED. Go to the Organizations page to assign it.');
        setShowCreateSiteModal(false);
        setNewSiteName('');
      }
    } catch (err: any) {
      alert(`Failed to create site: ${err.message}`);
    }
  };

  return (
    <div className="page sites-page">
      <div className="sites-header-row">
        <div className="sites-header-left">
          <h1 className="page__title">Sites</h1>
          <SearchableSelect
            id="site-select"
            value={selectedSiteId}
            onChange={(val) => {
              setSelectedSiteId(val);
              setActiveTab('home');
            }}
            options={sites.map(site => ({
              value: site.id,
              label: site.name,
              subLabel: site.tenantId === 'UNASSIGNED' ? 'Unassigned' : undefined
            }))}
            placeholder={sites.length > 0 ? "Choose a site..." : "You are not assigned to any sites"}
            disabled={sites.length === 0}
          />
        </div>
        {isSuperAdmin && (
          <button className="btn-create-site" onClick={() => setShowCreateSiteModal(true)}>
            Create Site
          </button>
        )}
      </div>

      {activeSite && (() => {
        let hasWriteAccess = isSuperAdmin;
        let canManageUsers = isSuperAdmin;

        if (!isSuperAdmin) {
          if (userRoles) {
            const siteRole = userRoles.find((r: any) => r.SK.includes(`SITE#${activeSite.id}`));
            const organizationRole = userRoles.find((role: { SK?: string; role?: string }) => role.SK === `ROLE#TENANT#${activeSite.tenantId}`);
            if (siteRole && ['LocalAdmin', 'SiteAdmin'].includes(siteRole.role)) {
              hasWriteAccess = true;
              canManageUsers = true;
            } else if (organizationRole?.role === 'OrgAdmin') {
              hasWriteAccess = true;
            }
          }
        }
        
        return (
        <>
          <div className="tab-bar">
            <button className={`tab-btn ${activeTab === 'home' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('home')}>Home</button>
            {canManageUsers && activeSite.tenantId !== 'UNASSIGNED' && (
              <button className={`tab-btn ${activeTab === 'users' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('users')}>Users</button>
            )}
            {hasWriteAccess && (
              <button className={`tab-btn ${activeTab === 'units' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('units')}>Units</button>
            )}
            {hasWriteAccess && (
              <button className={`tab-btn ${activeTab === 'config' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('config')}>Configuration</button>
            )}
          </div>

          <div className="tab-content-panel">
            {activeTab === 'home' && (
              <>
                <div className="card spreadsheet-card" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-primary)' }}>
                    <h3 className="card-title" style={{ margin: 0 }}>Units</h3>
                  </div>
                  <div className="spreadsheet-container">
                    <table className="spreadsheet-table">
                      <thead>
                        <tr>
                          <th onClick={() => toggleSort('nodeName')} className="sortable-header">
                            Node Name <span className="sort-arrow">{sortField === 'nodeName' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                          </th>
                          <th onClick={() => toggleSort('volume')} className="sortable-header">
                            Water Volume <span className="sort-arrow">{sortField === 'volume' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                          </th>
                          <th onClick={() => toggleSort('voltage')} className="sortable-header">
                            Voltage <span className="sort-arrow">{sortField === 'voltage' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                          </th>
                          <th onClick={() => toggleSort('lastSeenAt')} className="sortable-header">
                            Last Updated / Actions <span className="sort-arrow">{sortField === 'lastSeenAt' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                          </th>
                        </tr>
                        <tr className="filter-row">
                          <td>
                            <input
                              type="text"
                              placeholder="Filter Node Name..."
                              value={filterNodeName}
                              onChange={e => setFilterNodeName(e.target.value)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder="Filter Volume..."
                              value={filterVolume}
                              onChange={e => setFilterVolume(e.target.value)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              placeholder="Filter Voltage..."
                              value={filterVoltage}
                              onChange={e => setFilterVoltage(e.target.value)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td></td>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedUnits.length > 0 ? (
                          filteredAndSortedUnits.map(unit => {
                            // Charging status indicator arrow — only from two real
                            // voltage readings; no previous reading = no trend (D4).
                            let arrow = '';
                            if (unit.previousVoltage !== undefined && unit.previousVoltage !== 0) {
                              arrow = unit.currentVoltage > unit.previousVoltage ? ' ↑' : ' ↓';
                            }

                            // Voltage color class
                            let voltageColorClass = 'text-good';
                            if (unit.currentVoltage < 3.2) {
                              voltageColorClass = 'text-critical';
                            } else if (unit.currentVoltage < 3.5) {
                              voltageColorClass = 'text-warning';
                            }

                            return (
                              <tr key={unit.unitId}>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                      <Link 
                                        to={`/dashboard/units/${unit.unitId}`} 
                                        className="hyperlink"
                                        style={{ fontWeight: '600', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-base)', textDecoration: 'none' }}
                                      >
                                        {unit.nodeName}
                                      </Link>
                                      <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 6px', backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                                        {unit.isMobileStation ? 'Mobile Station' : 'Repeater'}
                                      </span>
                                    </div>
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>
                                      ID: {unit.nodeId}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  {unit.isMobileStation && unit.volumeLiters !== null && unit.percentFilled !== null ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                      <span style={{ fontWeight: '700', fontSize: 'var(--font-size-base)', color: 'var(--color-brand-primary)' }}>
                                        {unit.volumeLiters.toLocaleString()} L
                                      </span>
                                      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600' }}>
                                        ({unit.percentFilled}%)
                                      </span>
                                    </div>
                                  ) : unit.isMobileStation ? (
                                    <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 'bold' }}>{waterReadingGapLabel(unit, unit.currentLevel)}</span>
                                  ) : (
                                    <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 'bold' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  <span 
                                    className={voltageColorClass}
                                    style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}
                                  >
                                    {unit.currentVoltage.toFixed(2)} V{arrow}
                                  </span>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                      {formatTimeAgo(unit.lastSeenAt)}
                                    </span>
                                    <Link 
                                      to={`/dashboard/units/${unit.unitId}`} 
                                      className="hyperlink"
                                      style={{ fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-brand-primary)', textDecoration: 'none' }}
                                    >
                                      View Details →
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                              No units found matching current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card">
                  <SiteMap units={siteUnits as any} sites={[activeSite] as any} activeSiteId={activeSite.id} />
                </div>
              </>
            )}
 
            {activeTab === 'users' && (() => {
              const isCallerLocalAdmin = userRoles?.some((r: any) => r.SK.includes(`SITE#${activeSite.id}`) && r.role === 'LocalAdmin');

              return (
                <div className="users-tab-layout two-col-layout">
                  <div className="spreadsheet-column" style={{ flex: 2.3 }}>
                    <div className="card spreadsheet-card">
                      <div className="spreadsheet-container">
                        <table className="spreadsheet-table">
                          <thead>
                            <tr>
                              <th onClick={() => toggleUserSort('firstName')} className="sortable-header">
                                First Name <span className="sort-arrow">{sortUserField === 'firstName' ? (sortUserDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                              </th>
                              <th onClick={() => toggleUserSort('lastName')} className="sortable-header">
                                Last Name <span className="sort-arrow">{sortUserField === 'lastName' ? (sortUserDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                              </th>
                              <th onClick={() => toggleUserSort('email')} className="sortable-header">
                                Email <span className="sort-arrow">{sortUserField === 'email' ? (sortUserDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                              </th>
                              <th onClick={() => toggleUserSort('role')} className="sortable-header">
                                Access Level <span className="sort-arrow">{sortUserField === 'role' ? (sortUserDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                              </th>
                              <th>Actions</th>
                            </tr>
                            <tr className="filter-row">
                              <td>
                                <input
                                  type="text"
                                  placeholder="Filter First Name..."
                                  value={filterFirstName}
                                  onChange={e => setFilterFirstName(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Filter Last Name..."
                                  value={filterLastName}
                                  onChange={e => setFilterLastName(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Filter Email..."
                                  value={filterEmail}
                                  onChange={e => setFilterEmail(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Filter Level..."
                                  value={filterRole}
                                  onChange={e => setFilterRole(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                              <td></td>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAndSortedUsers.length > 0 ? (
                              filteredAndSortedUsers.map(u => {
                                const isSelf = u.userId === currentUserId;
                                const isTargetSuperAdmin = u.role === 'SuperAdmin';

                                const canRevokeUser = isSuperAdmin || (isCallerLocalAdmin && u.role === 'Viewer');
                                const canPromoteUser = isSuperAdmin && u.role === 'Viewer';

                                return (
                                  <tr key={u.userId}>
                                    <td>{u.firstName || '—'}</td>
                                    <td>{u.lastName || '—'}</td>
                                    <td className="email-cell">{u.email}</td>
                                    <td>{u.role === 'OrgAdmin' ? 'Org Admin' : u.role === 'LocalAdmin' ? 'Local Admin' : u.role === 'Viewer' ? 'Viewer' : u.role}</td>
                                    <td>
                                      <div className="action-buttons-cell">
                                        <button
                                          type="button"
                                          className="btn-promote-action"
                                          disabled={!canPromoteUser || isSelf}
                                          onClick={() => setConfirmAction({ type: 'promote', userId: u.userId, email: u.email })}
                                          title={u.role !== 'Viewer' ? 'Only Viewers can be promoted' : 'Promote user to Local Admin'}
                                        >
                                          Promote
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-revoke-action"
                                          disabled={!canRevokeUser || isSelf || isTargetSuperAdmin}
                                          onClick={() => setConfirmAction({ type: 'revoke', userId: u.userId, email: u.email })}
                                          title="Revoke access"
                                        >
                                          Revoke
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                                  No users found matching current filters.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-divider"></div>

                  <div className="invite-column" style={{ flex: 1, minWidth: '300px' }}>
                    <div className="card invite-card">
                      <form onSubmit={handleInvite}>
                        <div className="invite-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                          <div style={{ flex: 1, paddingRight: '1rem' }}>
                            <h3 className="card-title" style={{ margin: 0 }}>Invite</h3>
                            <p className="invite-desc" style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                              Users invited here will have access to <strong>{activeSite.name}</strong>.
                            </p>
                          </div>
                          <button type="submit" className="btn-send-invite" style={{ flexShrink: 0, marginTop: '4px' }}>Send Invite</button>
                        </div>
                        
                        <div className="invite-inputs-row" style={{ display: 'flex', gap: '0.75rem' }}>
                          <input 
                            type="email" 
                            placeholder="Email Address" 
                            value={inviteEmail} 
                            onChange={e => setInviteEmail(e.target.value)} 
                            required 
                            className="invite-email-input"
                            style={{ flex: 2 }}
                          />
                          <select 
                            value={inviteRole} 
                            onChange={e => setInviteRole(e.target.value as any)} 
                            required
                            className="invite-role-select"
                            style={{ flex: 1 }}
                          >
                            <option value="Viewer">Viewer</option>
                            {isSuperAdmin && <option value="LocalAdmin">Local Admin</option>}
                          </select>
                        </div>
                      </form>
                      {inviteMessage && (
                        <p style={{ 
                          marginTop: '1rem', 
                          color: inviteMessage.startsWith('Error') ? 'var(--color-status-critical)' : 'var(--color-brand-primary)',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}>
                          {inviteMessage}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {activeTab === 'units' && (
              <>
                {unadoptedUnits.length > 0 && (
                  <div className="unadopted-banner" onClick={openAdoptModal}>
                    <span>Adopt New Unit ({unadoptedUnits.length} unadopted units found)</span>
                    <span>Click to Adopt →</span>
                  </div>
                )}
                
                <div className="card">
                  <div className="config-form" style={{ maxWidth: '500px' }}>
                    <div className="form-group">
                      <button
                        type="button"
                        className="btn-save-changes"
                        style={{ marginBottom: '1rem' }}
                        onClick={() => { setAddUnitSerial(''); setAddUnitOpen(true); }}
                      >
                        Add Unit
                      </button>
                    </div>
                    {addUnitOpen && (
                      <div className="modal-overlay" onClick={() => setAddUnitOpen(false)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                          <h2>Add Unit</h2>
                          <form onSubmit={e => {
                            e.preventDefault();
                            const serial = addUnitSerial.trim();
                            if (serial) navigate(`/d/${encodeURIComponent(serial)}`);
                          }}>
                            <div className="form-group">
                              <label style={{ fontWeight: 'bold' }}>Serial Number</label>
                              <input
                                type="text"
                                value={addUnitSerial}
                                onChange={e => setAddUnitSerial(e.target.value)}
                                placeholder="e.g. RTM-000142-K7QX"
                                autoFocus
                                required
                              />
                              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                                Enter the serial number printed on the device label, or scan its QR code.
                              </p>
                            </div>
                            <div className="modal-actions">
                              <button type="button" className="btn-secondary" onClick={() => setAddUnitOpen(false)}>Cancel</button>
                              <button
                                type="submit"
                                className="btn-save-changes"
                                disabled={!addUnitSerial.trim()}
                                title={!addUnitSerial.trim() ? 'Enter the device serial number first.' : undefined}
                              >
                                Continue
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                    <div className="form-group">
                      <label style={{ fontWeight: 'bold' }}>Select Unit to Edit</label>
                      <SearchableSelect
                        value={editingUnitId}
                        onChange={(val) => handleEditUnitSelect(val)}
                        options={siteUnits.map(u => ({
                          value: u.unitId,
                          label: `${u.nodeName} (${u.unitId})`
                        }))}
                        placeholder="Choose a unit..."
                      />
                    </div>

                    {editingUnitId && (
                      <form onSubmit={handleSaveUnitConfig}>
                        <div className="form-group">
                          <label style={{ fontWeight: 'bold' }}>Node Name</label>
                          <input type="text" value={configForm.nodeName} onChange={e => setConfigForm({...configForm, nodeName: e.target.value})} required />
                        </div>
                        <div className="form-group" style={{ display: 'flex', gap: '1rem' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 'bold' }}>Latitude</label>
                            <input type="number" step="any" value={configForm.latitude} onChange={e => setConfigForm({...configForm, latitude: Number(e.target.value)})} required />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontWeight: 'bold' }}>Longitude</label>
                            <input type="number" step="any" value={configForm.longitude} onChange={e => setConfigForm({...configForm, longitude: Number(e.target.value)})} required />
                          </div>
                        </div>
                        <div className="form-group">
                          <label style={{ fontWeight: 'bold' }}>Max Battery Voltage (V)</label>
                          <input type="number" step="any" value={configForm.maxBatteryVoltage} onChange={e => setConfigForm({...configForm, maxBatteryVoltage: Number(e.target.value)})} required />
                        </div>
                        {siteUnits.find(u => u.unitId === editingUnitId)?.role !== 'leaf' && (
                          <div className="form-group">
                            <label style={{ fontWeight: 'bold' }}>Reporting Interval</label>
                            <select
                              value={configForm.cadenceSeconds}
                              onChange={e => setConfigForm({...configForm, cadenceSeconds: Number(e.target.value)})}
                            >
                              {CADENCE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {siteUnits.find(u => u.unitId === editingUnitId)?.unitType === UnitType.MobileStation && (
                          <>
                            <div className="form-group">
                              <label style={{ fontWeight: 'bold' }}>Tank Shape</label>
                              <select value={configForm.tankShape} onChange={e => setConfigForm({...configForm, tankShape: e.target.value})}>
                                <option value="cylindrical_vertical">Cylindrical Vertical</option>
                                <option value="cylindrical_horizontal">Cylindrical Horizontal</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label style={{ fontWeight: 'bold' }}>
                                {configForm.tankShape === 'cylindrical_horizontal' ? 'Vertical Diameter (cm)' : 'Horizontal Diameter (cm)'}
                              </label>
                              <input type="number" value={configForm.diameter} onChange={e => setConfigForm({...configForm, diameter: Number(e.target.value)})} required />
                            </div>
                            <div className="form-group">
                              <label style={{ fontWeight: 'bold' }}>
                                {configForm.tankShape === 'cylindrical_horizontal' ? 'Horizontal Length (cm)' : 'Vertical Height (cm)'}
                              </label>
                              <input type="number" value={configForm.height} onChange={e => setConfigForm({...configForm, height: Number(e.target.value)})} required />
                            </div>
                            <div className="form-group">
                              <label style={{ fontWeight: 'bold' }}>Sensor Mount Height (cm)</label>
                              <input type="number" min={0} value={configForm.sensorMountHeight} onChange={e => setConfigForm({...configForm, sensorMountHeight: Number(e.target.value)})} required />
                              <small style={{ display: 'block', marginTop: '0.25rem', color: 'var(--color-text-tertiary)' }}>
                                How far the sensor sits <strong>above the top of the tank</strong>. Enter 0 if it is
                                mounted level with the rim.
                                {configForm.height > 0 && (
                                  <> A full tank then reads {configForm.sensorMountHeight} cm and an empty one
                                  reads {Number(configForm.height) + Number(configForm.sensorMountHeight)} cm.</>
                                )}
                              </small>
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
                          <button type="submit" className="btn-save-changes">Save Configuration</button>
                          {(() => {
                            const selectedUnit = siteUnits.find(unit => unit.unitId === editingUnitId);
                            return selectedUnit ? (
                              <UnitLifecycleActions
                                unit={selectedUnit}
                                sites={sites}
                                onComplete={() => {
                                  setEditingUnitId('');
                                  refetch();
                                  void fetchUnadopted();
                                }}
                              />
                            ) : null;
                          })()}
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'config' && (
              <div className="card">
                <form onSubmit={handleSaveSiteConfig} className="config-form" style={{ maxWidth: '500px' }}>
                  <div className="form-group">
                    <label style={{ fontWeight: 'bold' }}>Site Name</label>
                    <input type="text" value={siteConfig.name} onChange={e => setSiteConfig({name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: 'bold' }}>Site API Key (For Field Gateways)</label>
                    <div className="api-key-box">
                      {activeSite.apiKey || 'No API Key available'}
                    </div>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      Use this key to authenticate MQTT connections from your hardware.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                    <button type="submit" className="btn-save-changes">Save Changes</button>
                    <button type="button" className="btn-danger" onClick={() => setConfirmDeleteSite(true)}>Delete Site</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </>
        );
      })()}

      {showAdoptModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Adopt Units</h2>
            <form onSubmit={handleAdopt} className="config-form">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Select the units you want to adopt into {activeSite?.name}.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem', maxHeight: '300px', overflowY: 'auto' }}>
                {unadoptedUnits.map(u => {
                  const uid = u.unitId || u.SK?.replace('UNIT#','');
                  return (
                    <label key={uid} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedUnadoptedUnits.includes(uid)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUnadoptedUnits([...selectedUnadoptedUnits, uid]);
                          } else {
                            setSelectedUnadoptedUnits(selectedUnadoptedUnits.filter(id => id !== uid));
                          }
                        }}
                      />
                      {u.telemetryNodeName || u.nodeName || uid}
                    </label>
                  );
                })}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAdoptModal(false)}>Cancel</button>
                <button type="submit" className="view-toggle-btn view-toggle-btn--active" disabled={selectedUnadoptedUnits.length === 0}>
                  Adopt Selected
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateSiteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create New Site</h2>
            <form onSubmit={handleCreateSite} className="config-form">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                {activeOrganization 
                  ? `Create a new site. It will be automatically assigned to ${activeOrganization.name}.`
                  : 'Create a global site. It will be Unassigned until assigned to an Organization.'}
              </p>
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label>Site Name</label>
                <input 
                  type="text" 
                  value={newSiteName} 
                  onChange={e => setNewSiteName(e.target.value)} 
                  placeholder="e.g. Norquay West Reservoir"
                  required 
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateSiteModal(false)}>Cancel</button>
                <button type="submit" className="view-toggle-btn view-toggle-btn--active" disabled={!newSiteName}>
                  Create Site
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>{confirmAction.type === 'revoke' ? 'Revoke User Access' : 'Promote User to Local Admin'}</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              {confirmAction.type === 'revoke' ? (
                <p>
                  Are you sure you want to revoke access for <strong>{confirmAction.email}</strong>? They will lose all access to this site.
                </p>
              ) : (
                <p>
                  Are you sure you want to promote <strong>{confirmAction.email}</strong> to Local Admin? They will have administrative privileges for this site.
                </p>
              )}
              {actionError && (
                <p className="error-message" style={{ color: 'var(--color-status-critical)', marginTop: '1rem', fontWeight: 500 }}>
                  {actionError}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setConfirmAction(null);
                  setActionError('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className={confirmAction.type === 'revoke' ? 'btn-danger' : 'btn-confirm-action'}
                onClick={handleConfirmAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : confirmAction.type === 'revoke' ? 'Revoke Access' : 'Promote User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Confirmation Modal */}
      {confirmDeleteSite && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>Delete Site</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>
                Are you sure you want to delete the site <strong>{activeSite?.name}</strong>? This action is permanent and cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setConfirmDeleteSite(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleDeleteSite}
              >
                Delete Site
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sites;
